import {
  CTA,
  DesignTokenSizeEnum,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import {
  performVWBasicButtonClick,
  performVWBasicLinkClick,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import useGlobalStore, {GlobalStoreStateConfiguration} from '@/stores/globalStore';
import { ButtonWrapper } from '@/styles';
import Box from '../../Box/Box';
import CTAMail from '../../CTAMail/CTAMail';
import CTATel from '../../CTATel/CTATel';
import DataList from '../../DataList/DataList';
import DataListRow from '../../DataList/DataListRow';
import {
  StyledFooterRetailerCtas,
  StyledFooterRetailerLinks,
  StyledFooterRetailerTimes,
} from './FooterRetailer.styles';
import { getActiveConfiguration } from '@/modules/utils/storeUtils';
import { useLocation } from 'react-router-dom';

interface FooterRetailerTimesProps {
  openingTimes: {
    day: string;
    times: string;
  }[] | undefined;
  emailRetailerCta?: boolean;
  contactRetailerCta?: boolean;
  contactEmail?: string;
  retailerAddress?: string;
  retailerNumber?: string;
  retailerNumberSize?: TokenTextAppearance;
  centerRetailerLinks?: boolean;
  sectionName?: SectionNames;
}

const FooterRetailerTimes = ({
  openingTimes,
  contactEmail,
  retailerAddress,
  contactRetailerCta,
  retailerNumber,
  centerRetailerLinks,
  emailRetailerCta = true,
  retailerNumberSize = TokenTextAppearance.copy100,
  sectionName,
}: FooterRetailerTimesProps) => {
  const { contentManager, analyticsBuilder, analyticsManager } =
    useTribalContext();
  const { configurations, retailers, fleetMode } = useGlobalStore((state) => state.app);
  const { app } = useGlobalStore();
  const activeConfiguration: GlobalStoreStateConfiguration | undefined = getActiveConfiguration(app);
  const location = useLocation();
  const emailRetailerCtaLabel = contentManager.get('ctas-email-retailer');
  const contactRetailerCtaLabel = contentManager.get('ctas-contact-retailer');
  const mapsLabel = contentManager.get('ctas-get-directions');
  const mapsLink = `https://maps.google.com/maps?q=${retailerAddress}`;

  const analyticsCommonFields = {
    sectionName,
    ...(location.pathname.includes('compare') && { configurations }),
    ...(!(location.pathname.includes('compare')) && { configuration: activeConfiguration }),
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  return (
    <StyledFooterRetailerTimes>
      <Box mb={DesignTokenSizeEnum.static250}>
        <DataList>
          {openingTimes?.map(({ day, times }) => (
            <DataListRow label={day} value={times} key={day} />
          ))}
        </DataList>
      </Box>
      <StyledFooterRetailerCtas>
        {!fleetMode && retailerNumber && (
          <Box mb={DesignTokenSizeEnum.static350}>
            <CTATel
              tel={retailerNumber}
              textProps={{ appearance: retailerNumberSize }}
              onClick={() =>
                performVWBasicButtonClick({
                  label: `${retailerNumber}`,
                  ...analyticsCommonFields,
                })
              }
            />
          </Box>
        )}
        {contactRetailerCta && contactEmail && (
          <Box mb={DesignTokenSizeEnum.static350}>
            <ButtonWrapper>
              <CTAMail
                ctaProps={{emphasis: "primary"}}
                mailto={contactEmail}
                onClick={() =>
                  performVWBasicButtonClick({
                    label: contactRetailerCtaLabel,
                    ...analyticsCommonFields,
                  })
                }
              >
                {contactRetailerCtaLabel}
              </CTAMail>
            </ButtonWrapper>
          </Box>
        )}
        <StyledFooterRetailerLinks center={centerRetailerLinks}>
          {emailRetailerCta && contactEmail && (
            <CTAMail
              
              ctaProps={{emphasis: "tertiary"}}
              mailto={contactEmail}
              onClick={() =>
                performVWBasicLinkClick({
                  label: emailRetailerCtaLabel,
                  ...analyticsCommonFields,
                })
              }
            >
              {emailRetailerCtaLabel}
            </CTAMail>
          )}
          <CTA
            tag="a"
            emphasis='tertiary'
            href={mapsLink}
            target="_blank"
            onClick={() =>
              performVWBasicLinkClick({
                label: mapsLabel,
                ...analyticsCommonFields,
              })
            }
          >
            {mapsLabel}
          </CTA>
        </StyledFooterRetailerLinks>
      </StyledFooterRetailerCtas>
    </StyledFooterRetailerTimes>
  );
};

export default FooterRetailerTimes;
